import type { FC } from 'react';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import isEqual from 'lodash/isEqual';

import { Body } from '@cognite/cogs-core-v11/tokens/typography/typography.tsx';
import { FilterChip } from '@cognite/cogs-core-v11/components/filter-chip/filter-chip.tsx';
import { Flex } from '@cognite/cogs-core-v11/components/flex/flex.tsx';
import { Infobox } from '@cognite/cogs-core-v11/components/infobox/infobox.tsx';
import { Select } from '@cognite/cogs-core-v11/components/select/select.tsx';

import { ComponentContainer } from '../../../../ui/component/component-container/component-container';
import { useUpsertAppConfigMutation } from '../../../hooks/use-mutation/use-upsert-app-config-mutation/use-upsert-app-config-mutation';
import { useAppConfigQuery } from '../../../hooks/use-query/use-app-config-query';
import { getGroupsQueryContext } from '../../../hooks/use-query/use-groups-query-context';
import { SaveConfigurationButton } from '../../../save-configuration/save-configuration-button';
import type { AppConfig } from '../../../types';
import { getIsNewConfigVersion } from '../../../utils/utils';

import * as S from './elements';

interface Props {
  hasAppConfigPermission: boolean;
  rootLocationExternalId: string;
}

export const RootLocationTemplateChecklistAdmins: FC<Props> = ({
  hasAppConfigPermission,
  rootLocationExternalId,
}) => {
  const [templateAdminGroups, setTemplateAdminGroups] = useState<string[]>();
  const [checklistAdminGroups, setChecklistAdminGroups] = useState<string[]>();
  const { data: appConfig, isSuccess, isLoading } = useAppConfigQuery();
  const isNewConfigVersion = getIsNewConfigVersion(appConfig);
  const { mutateAsync: upsertAppConfig } = useUpsertAppConfigMutation();
  const { t } = useTranslation('configuration');
  const configuredRootLocation =
    appConfig?.featureConfiguration?.rootLocationConfigurations?.find(
      (rootLocation) =>
        rootLocationExternalId ===
        (isNewConfigVersion
          ? rootLocation.externalId
          : rootLocation.assetExternalId)
    );
  const { useGroupsQuery } = useContext(getGroupsQueryContext());
  const { data: groups } = useGroupsQuery();

  const templateAdminOptions = groups
    ?.filter((group) => !templateAdminGroups?.includes(group.name))
    .map((group) => ({
      label: group.name,
      value: group.name,
    }));

  const checklistAdminOptions = groups
    ?.filter((group) => !checklistAdminGroups?.includes(group.name))
    .map((group) => ({
      label: group.name,
      value: group.name,
    }));

  useEffect(() => {
    if (isSuccess && configuredRootLocation) {
      if (configuredRootLocation.templateAdmins) {
        setTemplateAdminGroups(configuredRootLocation.templateAdmins);
      }
      if (configuredRootLocation.checklistAdmins) {
        setChecklistAdminGroups(configuredRootLocation.checklistAdmins);
      }
    }
  }, [isSuccess, configuredRootLocation, appConfig]);

  const hasConfigChanged =
    !isEqual(templateAdminGroups, configuredRootLocation?.templateAdmins) ||
    !isEqual(checklistAdminGroups, configuredRootLocation?.checklistAdmins);

  const handleSave = async () => {
    if (!appConfig) return;

    const newAppConfig: AppConfig = {
      ...appConfig,
      featureConfiguration: {
        ...appConfig.featureConfiguration,
        rootLocationConfigurations: [
          ...(appConfig.featureConfiguration?.rootLocationConfigurations?.map(
            (rootConfig) => {
              if (
                isNewConfigVersion
                  ? configuredRootLocation?.externalId === rootConfig.externalId
                  : configuredRootLocation?.assetExternalId ===
                    rootConfig.assetExternalId
              ) {
                return {
                  ...rootConfig,
                  templateAdmins: templateAdminGroups,
                  checklistAdmins: checklistAdminGroups,
                };
              }
              return { ...rootConfig };
            }
          ) || []),
        ],
      },
    };

    await upsertAppConfig({
      newAppConfig,
    });
  };

  const templateAdminConfigs = (
    <Flex direction="column" gap={24}>
      <Body size="x-small">
        {t(
          'RESTRICT_WHO_HAS_ACCESS_INFO',
          `Restrict who has access to create / 
           edit templates and mark checklists as done to a set of users by referring
           to CDF group names that holds the users who
           shall have access. If no group is configured, everyone will have access. `
        )}
      </Body>
      <Flex gap={16}>
        <Flex direction="column" gap={8}>
          <Body size="x-small" strong>
            {t('TEMPLATE_ADMINS', 'Template admins')}
          </Body>
          <Select
            disabled={!hasAppConfigPermission}
            options={templateAdminOptions ?? []}
            onChange={(e: { label: string; value: string }) =>
              setTemplateAdminGroups((prev) => [...(prev ?? []), e.value])
            }
            width={500}
            placeholderElement={
              <div>
                {t('SEARCH_AND_SELECT_GROUP', 'Search and select group')}
              </div>
            }
          />
          <Flex gap={8}>
            {templateAdminGroups?.map((group) => (
              <FilterChip
                key={group}
                label={group}
                onRemove={
                  !hasAppConfigPermission
                    ? undefined
                    : () =>
                        setTemplateAdminGroups((prev) =>
                          prev?.filter((prevGroup) => prevGroup !== group)
                        )
                }
              />
            ))}
          </Flex>
        </Flex>
      </Flex>
      <Flex gap={16}>
        <Flex direction="column" gap={8}>
          <Body size="x-small" strong>
            {t(`CHECKLIST_ADMINS`, 'Checklist admins')}
          </Body>
          <Select
            disabled={!hasAppConfigPermission}
            options={checklistAdminOptions ?? []}
            onChange={(e: { label: string; value: string }) =>
              setChecklistAdminGroups((prev) => [...(prev ?? []), e.value])
            }
            width={500}
            placeholderElement={
              <div>
                {t('SEARCH_AND_SELECT_GROUP', 'Search and select group')}
              </div>
            }
          />
          <Flex gap={8}>
            {checklistAdminGroups?.map((group) => (
              <FilterChip
                key={group}
                label={group}
                onRemove={
                  !hasAppConfigPermission
                    ? undefined
                    : () =>
                        setChecklistAdminGroups((prev) =>
                          prev?.filter((prevGroup) => prevGroup !== group)
                        )
                }
              />
            ))}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
  return (
    <ComponentContainer>
      <S.InfoBoxWrapper>
        <Flex direction="column" gap={16}>
          {templateAdminConfigs}
          {hasConfigChanged && (
            <Infobox status="warning" compact>
              {t(
                'WARNING_RESTRICTION_ONLY_ON_APP',
                `Admin user groups you assign and their access capabilities apply to InField only. This restriction does not change any access management configured for Cognite Data Fusion. `
              )}
            </Infobox>
          )}
          <SaveConfigurationButton
            onSave={handleSave}
            isDisabled={!hasConfigChanged}
            isLoading={isLoading}
          />
        </Flex>
      </S.InfoBoxWrapper>
    </ComponentContainer>
  );
};
